import React, { useState, useEffect } from "react";
import Label from "../fields/Label";
import { MdAccessTime } from "react-icons/md";
import { TrialEntitlement } from "../../types/financial";
import {
  DEFAULT_TRIAL_ENTITLEMENTS,
  TRIAL_ENTITLEMENT_OPTIONS,
  getEntitlementLabel,
} from "../../utils/trialEntitlements";
import Button from "../common/Button";
import ModalShell from "../modals/ModalShell";

interface GrantTrialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (trialDays: number, entitlements: TrialEntitlement[]) => void;
  userName: string;
  loading?: boolean;
  error?: string | null;
}

const PRESET_OPTIONS = [
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
  { value: 30, label: "30 days" },
  { value: 60, label: "60 days" },
  { value: 90, label: "90 days" },
];

const MAX_TRIAL_DAYS = 180;

const GrantTrialModal: React.FC<GrantTrialModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  userName,
  loading = false,
  error = null,
}) => {
  const [trialDays, setTrialDays] = useState<number>(7);
  const [customDays, setCustomDays] = useState<string>("");
  const [useCustom, setUseCustom] = useState(false);
  const [entitlements, setEntitlements] = useState<TrialEntitlement[]>(
    DEFAULT_TRIAL_ENTITLEMENTS
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setTrialDays(7);
      setCustomDays("");
      setUseCustom(false);
      setEntitlements(DEFAULT_TRIAL_ENTITLEMENTS);
      setValidationError(null);
    }
  }, [isOpen]);

  const handlePresetSelect = (days: number) => {
    setTrialDays(days);
    setUseCustom(false);
    setCustomDays("");
    setValidationError(null);
  };

  const toggleEntitlement = (value: TrialEntitlement) => {
    setValidationError(null);
    setEntitlements((prev) =>
      prev.includes(value)
        ? prev.filter((entitlement) => entitlement !== value)
        : [...prev, value]
    );
  };

  const handleCustomChange = (value: string) => {
    setCustomDays(value);
    setUseCustom(true);
    setValidationError(null);

    const numValue = parseInt(value, 10);
    if (value && !isNaN(numValue)) {
      if (numValue < 1) {
        setValidationError("Trial duration must be at least 1 day");
      } else if (numValue > MAX_TRIAL_DAYS) {
        setValidationError(
          `Trial duration cannot exceed ${MAX_TRIAL_DAYS} days`
        );
      } else {
        setTrialDays(numValue);
      }
    }
  };

  const handleSubmit = () => {
    const finalDays = useCustom ? parseInt(customDays, 10) : trialDays;

    if (isNaN(finalDays) || finalDays < 1) {
      setValidationError("Please enter a valid number of days");
      return;
    }

    if (finalDays > MAX_TRIAL_DAYS) {
      setValidationError(`Trial duration cannot exceed ${MAX_TRIAL_DAYS} days`);
      return;
    }

    if (entitlements.length === 0) {
      setValidationError("Select at least one entitlement");
      return;
    }

    onSubmit(finalDays, entitlements);
  };

  const isValid = () => {
    if (entitlements.length === 0) {
      return false;
    }
    if (useCustom) {
      const numValue = parseInt(customDays, 10);
      return !isNaN(numValue) && numValue >= 1 && numValue <= MAX_TRIAL_DAYS;
    }
    return trialDays >= 1 && trialDays <= MAX_TRIAL_DAYS;
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Grant Trial Access"
      icon={<MdAccessTime className="h-6 w-6 text-biafit-primary" />}
      headerVariant="compact"
      size="md"
      layout="fixed"
      closeDisabled={loading}
      message={
        validationError
          ? { text: validationError, type: "error" }
          : error
          ? { text: error, type: "error" }
          : null
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={loading}
            loadingLabel="Granting..."
            disabled={!isValid()}
          >
            Grant Trial
          </Button>
        </>
      }
    >
      {/* Content */}
      <div className="p-6">
        <p className="mb-4 text-gray-600 dark:text-gray-300">
          Grant trial access to{" "}
          <span className="font-semibold text-navy-700 dark:text-white">
            {userName}
          </span>
        </p>

        {/* Preset Options */}
        <div className="mb-4">
          <Label>Select Duration</Label>
          <div className="grid grid-cols-3 gap-2">
            {PRESET_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handlePresetSelect(option.value)}
                className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                  !useCustom && trialDays === option.value
                    ? "border-biafit-primary bg-biafit-primary text-white"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Input */}
        <div className="mb-4">
          <Label>Or Enter Custom Duration</Label>
          <div className="flex items-center space-x-2">
            <input
              type="number"
              min="1"
              max={MAX_TRIAL_DAYS}
              value={customDays}
              onChange={(e) => handleCustomChange(e.target.value)}
              placeholder="Enter days"
              className={`flex-1 rounded-md border px-3 py-2 shadow-sm focus:border-biafit-primary focus:outline-none focus:ring-2 focus:ring-biafit-primary dark:bg-navy-700 dark:text-white dark:[color-scheme:dark] ${
                useCustom && validationError
                  ? "border-red-500"
                  : "border-gray-300 dark:border-gray-600"
              }`}
            />
            <span className="text-sm text-gray-500 dark:text-gray-400">
              days
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Maximum: {MAX_TRIAL_DAYS} days
          </p>
        </div>

        {/* Entitlements */}
        <div className="mb-4">
          <Label>Entitlements</Label>
          <div className="grid grid-cols-2 gap-2">
            {TRIAL_ENTITLEMENT_OPTIONS.map((option) => {
              const selected = entitlements.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleEntitlement(option.value)}
                  className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                    selected
                      ? "border-biafit-primary bg-biafit-primary text-white"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Summary */}
        {isValid() && (
          <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-900/20">
            <p className="text-sm text-green-700 dark:text-green-300">
              This will grant{" "}
              <span className="font-semibold">
                {useCustom ? customDays : trialDays} days
              </span>{" "}
              of trial access for{" "}
              <span className="font-semibold">
                {entitlements.map(getEntitlementLabel).join(", ")}
              </span>{" "}
              starting immediately.
            </p>
          </div>
        )}
      </div>
    </ModalShell>
  );
};

export default GrantTrialModal;
